import React, {Component} from 'react';
import {Table, Card, Loader, Input, Checkbox, Icon} from 'semantic-ui-react';
import {cloneDeep} from 'lodash';
import {headerInputCells, headerCheckboxCells} from '../static/commonStructures';
import {
  checkClassficationName,
  checkValidRowRange,
  checkFieldPattern,
  checkRepeatedPrimaryKey,
} from '../utils';
import './obsolescenceParameter.scss';

const headerCellTitlesOfData = headerInputCells.concat(headerCheckboxCells);
const daysChecker = {'includedStr': 'days', 'patternOfComparison': /^\d+$/i};
const priceFactorChecker = {'includedStr': 'priceFactor', 'patternOfComparison': /^[+]?\d+[.]?\d{0,3}$/i};

class ObsolescenceParametersTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      'obsParams': [{}],
    };
    this.addItem = this.addItem.bind(this);
  }

  componentDidMount() {
    const {obsParamList, disableSaveButton} = this.props;
    const deepCopyOfObsParams = cloneDeep(obsParamList);
    this.setState({'obsParams': deepCopyOfObsParams});
    disableSaveButton(false);
  }

  componentDidUpdate(prevProps, prevState) {
    const {disableSaveButton, setObsParams, obsParamList} = this.props;
    if (prevState.obsParams !== this.state.obsParams) {
      const {obsParams} = this.state;
      const rowsToRenderAlert = this.getRowsWithInvalidDays();
      const deepCopyOfObsParams = cloneDeep(obsParams);
      obsParams.some((row, rowIndex) => {
        disableSaveButton(this.isRowInvalid(row) || rowsToRenderAlert.length > 0);
        return (this.isRowInvalid(row) || rowsToRenderAlert.length > 0);
      });
      setObsParams(deepCopyOfObsParams);
    }
    
    if (prevProps.obsParamList !== obsParamList) {
      const deepCopyOfObsParams = cloneDeep(obsParamList);
      this.setState({'obsParams': deepCopyOfObsParams});
    }
  }
  
  isRowInvalid(row) {
    const {obsParams} = this.state;
    const numericalFieldConstraints = checkFieldPattern(row, 'daysFrom', daysChecker) ||
      checkFieldPattern(row, 'daysUntil', daysChecker) ||
      checkFieldPattern(row, 'priceFactor', priceFactorChecker) ||
      checkRepeatedPrimaryKey(row, obsParams) ||
      checkValidRowRange(row);

    const requiredConstraints = (!row.classification || !row.daysFrom || !row.daysUntil || !row.priceFactor);

    const obsParamNameConstraint = checkClassficationName(row);
    return (numericalFieldConstraints || requiredConstraints || obsParamNameConstraint);
  }

  getDayValuesList(obsParams) {
    return obsParams.reduce((acc, item) => {
      acc.push(item.daysFrom);
      acc.push(item.daysUntil);
      return acc;
    }, []);
  }

  getIndexesWithError(dayValuesList) {
    const indexesWithError = [];
    const minIterableSize = 4;
    if (dayValuesList.length < minIterableSize) {
      return indexesWithError;
    }
    // We iterate this array with step of 2 because we need to compare the daysFrom of the row i with the
    // daysUntil of the row i+1 for each couple of consecutive rows.
    for (let i = 1; i < dayValuesList.length - 1; i = i + 2) {
      if (Number(dayValuesList[i]) !== Number(dayValuesList[i + 1]) - 1) {
        indexesWithError.push(i);
      }
    }
    return indexesWithError;
  }

  getRowsToRenderAlert(indexesWithError) {
    return indexesWithError.map((indexWithError) => {
      return (indexWithError - 1) / 2;
    });
  }

  getRowsWithInvalidDays() {
    const {obsParams} = this.state;
    const dayValuesList = this.getDayValuesList(obsParams);
    const indexesWithError = this.getIndexesWithError(dayValuesList);
    return this.getRowsToRenderAlert(indexesWithError);
  }

  checkDaysRange(rowIndex, serializedName) {
    if (!serializedName.includes('days')) {
      return false;
    }
    const rowsToRenderAlert = this.getRowsWithInvalidDays();
    if (serializedName === 'daysUntil') {
      return rowsToRenderAlert.includes(rowIndex);
    }
    if (serializedName === 'daysFrom') {
      return rowsToRenderAlert.includes(rowIndex - 1);
    }
  }

  renderTable() {
    return (
      <Card fluid raised>
        <div className='obs-parameters-table'>
          <Table>
            {this.renderTableHeader()}
            {this.renderTableBody()}
          </Table>
          {this.renderAddItem()}
        </div>
      </Card>
    );
  }

  renderTableHeader() {
    return (
      <Table.Header>
        <Table.Row>
          {this.renderTableHeaderCells()}
        </Table.Row>
      </Table.Header>
    );
  }

  renderTableHeaderCells() {
    return (
      headerCellTitlesOfData.map((element) => {
        return (
          <Table.HeaderCell key={`header${element.name}`}>
            {element.name}
          </Table.HeaderCell>
        );
      })
    );
  }

  renderTableBody() {
    const {loading} = this.props;
    return (
      <Table.Body>
        {loading ? this.renderLoader() : this.renderObsParams()}
      </Table.Body>
    );
  }

  renderLoader() {
    return (
      <Table.Row>
        <Table.Cell>
          <Loader active inline='centered' />
        </Table.Cell>
      </Table.Row>
    );
  }

  renderObsParams() {
    const {obsParams} = this.state;
    if (!obsParams) {
      return null;
    }

    return obsParams.map((row, rowIndex) => {
      return (
        <Table.Row key={`row${rowIndex}`}>
          {this.renderInputInCells(row, rowIndex)}
          {this.renderCheckboxInCells(row, rowIndex)}
        </Table.Row>
      );
    });
  }

  renderInputInCells(element, rowIndex) {
    const {obsParams} = this.state;
    return (
      headerInputCells.map((title, columnIndex) => {
        const position = {'rowIndex': rowIndex, 'serializedName': title.serializedName};
        const duplicatedPrimaryKey = checkRepeatedPrimaryKey(element, obsParams, title.serializedName);
        const invalidDays = checkFieldPattern(element, title.serializedName, daysChecker);
        const invalidPriceFactor = checkFieldPattern(element, title.serializedName, priceFactorChecker);
        const invalidRangeDay = checkValidRowRange(element, title.serializedName);
        const renderInvalidDaysAlert = this.checkDaysRange(rowIndex, title.serializedName);
        const renderInvalidClassification = checkClassficationName(element, title.serializedName);
        return (
          <Table.Cell
            textAlign='center'
            key={`element${rowIndex}${columnIndex}`}
            style={{'width': title.width}}
          >
            <Input
              value={element[title.serializedName] || ''}
              disabled={this.isInputDisabled(element, title)}
              style={{'width': title.width}}
              onChange={(event) => this.handleChangeValue(event, position)}
              required={title.required}
              maxLength={title.maxLength}
            />
            {this.renderErrorMessage(duplicatedPrimaryKey, 'Nombre de parámetro repetido')}
            {this.renderErrorMessage(invalidDays, 'Ingrese valores enteros mayores que cero')}
            {this.renderErrorMessage(invalidPriceFactor, 'Ingrese valores mayores que cero con hasta tres decimales')}
            {this.renderErrorMessage(invalidRangeDay, 'Este valor debe ser mayor que los días desde')}
            {this.renderErrorMessage(renderInvalidDaysAlert, 'Los valores de estos campos deben ser contiguos')}
            {this.renderErrorMessage(renderInvalidClassification, 'El nombre debe empezar con OBS')}
          </Table.Cell>
        );
      })
    );
  }

  renderErrorMessage(displayErrorMessage, message) {
    if (displayErrorMessage) {
      return this.renderRedAlertMessage(message);
    }
  }

  renderRedAlertMessage(message) {
    return (
      <div className='alert'>
        {message}
      </div>
    );
  }

  isInputDisabled(element, column) {
    return (column.serializedName === 'classification' && !element.new);
  }

  handleChangeValue(event, position) {
    const {rowIndex, serializedName} = position;
    const {obsParams} = this.state;
    const deepCopyOfObsParams = cloneDeep(obsParams);
    deepCopyOfObsParams[rowIndex][serializedName] = event.target.value.toUpperCase();
    this.setState({'obsParams': deepCopyOfObsParams});
  }

  renderCheckboxInCells(element, rowIndex) {
    return (
      headerCheckboxCells.map((title, columnIndex) => {
        return (
          <Table.Cell
            textAlign='center'
            key={`element${rowIndex}${columnIndex}`}
            style={{'width': title.width}}
          >
            <Checkbox
              toggle
              checked={element[title.serializedName]}
              style={{'width': title.width}}
              onChange={(e, {checked}) => this.handleToggle(checked, rowIndex)}
            />
          </Table.Cell>
        );
      })
    );
  }

  handleToggle(checked, rowIndex) {
    const {obsParams} = this.state;
    const deepCopyOfObsParams = cloneDeep(obsParams);
    deepCopyOfObsParams[rowIndex].status = checked;
    this.setState({'obsParams': deepCopyOfObsParams});
  }

  renderAddItem() {
    return (
      <div className='addItem' onClick={this.addItem.bind(this)}>
        <Icon name='add' />
        <strong>AGREGAR REGISTRO</strong>
      </div>
    );
  }

  addItem() {
    const {obsParams} = this.state;
    const updatedObsParams = cloneDeep(obsParams);
    const defaultArray = {
      'classification': '', 'daysFrom': '', 'daysUntil': 9999, 'priceFactor': '', 'status': true, 'new': true,
    };
    this.setState({'obsParams': [...updatedObsParams, defaultArray]});
  }

  render() {
    return (
      this.renderTable()
    );
  }
}

export default ObsolescenceParametersTable;
